import { Controller } from "@hotwired/stimulus";

// Smart status change controller.
// On a status transition (Etude → Signé/Terminé, Phase → En cours/Terminée)
// it pops a small confirmation card asking for the relevant date, defaulting
// to today, then PATCHes the resource. The user can confirm directly or pick
// another date — that's the "guidance, not paperwork" feel we want.
export default class extends Controller {
  prompt(event) {
    event.preventDefault();
    const params = event.params;
    this._open({
      title: `Passer l'étude en « ${params.targetStatus} »`,
      dateLabel: this._etudeDateLabel(params.targetStatus),
      dateField: this._etudeDateField(params.targetStatus),
      url: params.updateUrl,
      method: "PATCH",
      payload: { etude: { statut: params.targetStatus } },
      resource: "etude",
    });
  }

  promptPhase(event) {
    event.preventDefault();
    const params = event.params;
    this._open({
      title: `Phase → « ${params.targetStatus} »`,
      dateLabel: params.targetStatus === "Terminée" ? "Date de fin réelle ?" : "Date de démarrage ?",
      dateField: params.targetStatus === "Terminée" ? "date_fin" : "date_debut",
      url: params.updateUrl,
      method: "PATCH",
      payload: { phase: { statut: params.targetStatus } },
      resource: "phase",
    });
  }

  _etudeDateLabel(status) {
    if (status === "Signé") return "Date de signature ?";
    // "Terminé" : Etude n'a pas de colonne de date de fin, donc on ne demande
    // (et n'envoie) aucune date — sinon update plantait en 500 (date_fin inconnu).
    return null;
  }

  _etudeDateField(status) {
    if (status === "Signé") return "date_signature";
    return null;
  }

  _open({ title, dateLabel, dateField, url, method, payload, resource }) {
    this._close();

    const overlay = document.createElement("div");
    overlay.className = "esm_smart-overlay";
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) this._close();
    });

    const card = document.createElement("div");
    card.className = "esm_smart-card";

    const todayISO = new Date().toISOString().slice(0, 10);
    const dateInputId = `smart-date-${Date.now()}`;

    card.innerHTML = `
      <button type="button" class="esm_smart-card__close" aria-label="Fermer">
        <i class="fa-solid fa-times"></i>
      </button>
      <div class="esm_smart-card__title">${this._escape(title)}</div>
      ${
        dateLabel
          ? `
        <label class="esm_smart-card__label" for="${dateInputId}">${this._escape(dateLabel)}</label>
        <input id="${dateInputId}" type="date" class="esm_smart-card__date" value="${todayISO}" />
        <div class="esm_smart-card__hint">Par défaut : aujourd'hui (${this._formatFR(todayISO)}). Ajuste si besoin.</div>
      `
          : `<div class="esm_smart-card__hint">Confirme la transition pour continuer.</div>`
      }
      <div class="esm_smart-card__actions">
        <button type="button" class="esm_btn esm_btn--ghost" data-role="cancel">Annuler</button>
        <button type="button" class="esm_btn esm_btn--primary" data-role="confirm">
          <i class="fa-solid fa-check"></i> Confirmer
        </button>
      </div>
    `;

    overlay.appendChild(card);
    document.body.appendChild(overlay);
    this._overlay = overlay;

    card.querySelector(".esm_smart-card__close").addEventListener("click", () => this._close());
    card.querySelector("[data-role='cancel']").addEventListener("click", () => this._close());
    card.querySelector("[data-role='confirm']").addEventListener("click", () => {
      const dateInput = card.querySelector(".esm_smart-card__date");
      const finalPayload = { ...payload };
      if (dateInput && dateField) {
        finalPayload[resource][dateField] = dateInput.value;
      }
      this._submit(url, method, finalPayload);
    });

    setTimeout(() => card.classList.add("esm_smart-card--in"), 10);
  }

  _close() {
    if (this._overlay) {
      this._overlay.remove();
      this._overlay = null;
    }
  }

  _submit(url, method, payload) {
    const token = document.querySelector("meta[name='csrf-token']")?.content;
    fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "text/html, application/json",
        "X-CSRF-Token": token,
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(payload),
      credentials: "same-origin",
    })
      .then((response) => {
        if (response.ok || response.redirected) {
          window.location.reload();
        } else {
          this._close();
          alert("La mise à jour a échoué. Vérifie les valeurs et réessaie.");
        }
      })
      .catch(() => {
        this._close();
        alert("Erreur réseau pendant la mise à jour.");
      });
  }

  _formatFR(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  }

  _escape(s) {
    const div = document.createElement("div");
    div.textContent = s;
    return div.innerHTML;
  }
}
